import React, { useState, useEffect } from 'react';
import { X, User, Mail, Save, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiService, type User as UserType } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

interface UserProfileProps {
  user: UserType;
  onClose: () => void;
  onUpdate: (user: UserType) => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ user, onClose, onUpdate }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    avatar_url: user.avatar_url || '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await apiService.updateProfile(formData);

      if (response.success && response.user) {
        setSuccess(true);
        onUpdate(response.user);

        // Cerrar modal después de 1.5 segundos
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(response.error || 'Error al actualizar perfil');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error inesperado');
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges = () => {
    return (
      formData.first_name !== (user.first_name || '') ||
      formData.last_name !== (user.last_name || '') ||
      formData.avatar_url !== (user.avatar_url || '')
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className={`${theme.colors.bg.input} rounded-2xl shadow-xl max-w-md w-full relative z-[10000]`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${theme.colors.border.primary}`}
        >
          <h2 className={`text-xl font-semibold ${theme.colors.text.primary} flex items-center`}>
            <User className="w-5 h-5 mr-2" />
            Mi Perfil
          </h2>
          <button
            onClick={onClose}
            className={`p-2 hover:${theme.colors.bg.secondary} rounded-lg transition-colors ${theme.colors.text.secondary} hover:${theme.colors.text.primary}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Success Message */}
          {success && (
            <div
              className={`p-3 ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg flex items-center bg-green-900/20 border-green-800`}
            >
              <CheckCircle2 className="w-4 h-4 text-nb-success mr-2 flex-shrink-0" />
              <span className={`text-sm ${theme.colors.text.primary} text-nb-success`}>
                Perfil actualizado correctamente
              </span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div
              className={`p-3 ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg flex items-center bg-red-900/20`}
            >
              <AlertCircle className={`w-4 h-4 ${theme.colors.action.danger} mr-2 flex-shrink-0`} />
              <span className={`text-sm ${theme.colors.action.danger}`}>{error}</span>
            </div>
          )}

          {/* Read-only Fields */}
          <div>
            <label className={`block text-sm font-medium ${theme.colors.text.primary} mb-2`}>
              Nombre de Usuario
            </label>
            <div
              className={`px-4 py-3 ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg`}
            >
              <p className={`text-sm ${theme.colors.text.secondary}`}>{user.username}</p>
            </div>
          </div>

          <div>
            <label
              className={`block text-sm font-medium ${theme.colors.text.primary} mb-2 flex items-center`}
            >
              <Mail className="w-4 h-4 mr-1" />
              Email
            </label>
            <div
              className={`px-4 py-3 ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg`}
            >
              <p className={`text-sm ${theme.colors.text.secondary}`}>{user.email}</p>
            </div>
          </div>

          {/* Editable Fields */}
          <div>
            <label
              htmlFor="user-first-name"
              className={`block text-sm font-medium ${theme.colors.text.primary} mb-2`}
            >
              Nombre
            </label>
            <input
              type="text"
              id="user-first-name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 ${theme.colors.bg.input} border ${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-nb-accent focus:border-transparent transition-all ${theme.colors.text.primary}`}
              placeholder="Tu nombre"
              disabled={isLoading}
            />
          </div>

          <div>
            <label
              htmlFor="user-last-name"
              className={`block text-sm font-medium ${theme.colors.text.primary} mb-2`}
            >
              Apellido
            </label>
            <input
              type="text"
              id="user-last-name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 ${theme.colors.bg.input} border ${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-nb-accent focus:border-transparent transition-all ${theme.colors.text.primary}`}
              placeholder="Tu apellido"
              disabled={isLoading}
            />
          </div>

          <div>
            <label
              htmlFor="user-avatar-url"
              className={`block text-sm font-medium ${theme.colors.text.primary} mb-2`}
            >
              URL del Avatar (opcional)
            </label>
            <input
              type="url"
              id="user-avatar-url"
              name="avatar_url"
              value={formData.avatar_url}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 ${theme.colors.bg.input} border ${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-nb-accent focus:border-transparent transition-all ${theme.colors.text.primary}`}
              placeholder="https://ejemplo.com/avatar.jpg"
              disabled={isLoading}
            />
            <p className={`text-xs ${theme.colors.text.secondary} mt-1`}>
              URL de tu imagen de perfil
            </p>
          </div>

          {/* Account Info */}
          <div className={`pt-4 border-t ${theme.colors.border.primary}`}>
            <p className={`text-xs ${theme.colors.text.secondary}`}>
              Cuenta creada el{' '}
              {new Date(user.created_at).toLocaleDateString('es', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`flex-1 px-4 py-3 border ${theme.colors.border.primary} ${theme.colors.text.primary} rounded-lg hover:${theme.colors.bg.secondary} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || !hasChanges()}
              className={`flex-1 bg-nb-accent hover:bg-nb-accent/90 ${theme.colors.text.inverse} font-medium py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;
